import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  CreateSwapsParams,
  useCreateSwap,
  useDeleteSwap, useGetPersonaProfiles,
  useGetSwapCountInProgress,
  useGetUser,
  useQuerySwaps, useUpdateSwapStatus,

} from '../../hooks/api-hooks';
import ConfirmModal from '../../partials/confirm-modal';
import SwapCard from './swap-card';
import FilterChips from './filter-chips';
import UploadArea from './upload-area';
import Pagination from './pagination';
import { InformationCircleIcon, VideoCameraIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { handleDownloadSwap } from '../../utils';
import SwapPreviewModal from './swap-preview-modal';
import { toast } from 'react-toastify';
import { SwapResponse } from '../../api/types';
import PhotoGuidelinesModalAnonymize from './photo-guidelines-anonymize-modal';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FeedbackModal from '../../partials/feedback-modal';

const MAX_UPLOADS = 4;
const ITEMS_PER_PAGE = 20;

export default function SwapsContainer() {
  // Pagination
  const [cursor, setCursor] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const location = useLocation();
  const locationData = location.state?.fromCreate;
  const navigate = useNavigate();
  const [isFeedbackOpen, setFeedbackOpen] = useState(false);


  // Filters
  const [isFavoriteSelected, setIsFavoriteSelected] = useState<boolean>(false);
  const [isMediaTypeImageSelected, setIsMediaTypeImageSelected] = useState<boolean>(false);
  const [isMediaTypeVideoSelected, setIsMediaTypeVideoSelected] = useState<boolean>(false);

  // Drag and Drop
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Sorting and Filtering Options
  const [sortOption, setSortOption] = useState<'createdAt' | 'status'>('createdAt');
  const [filterOption, setFilterOption] = useState<'all' | 'processing' | 'completed' | 'failed'>('all');

  // Media Preview Modal
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedMediaUrl, setSelectedMediaUrl] = useState<string>('');
  const [selectedOriginalUrl, setSelectedOriginalUrl] = useState<string | null>('');
  const [showOriginal, setShowOriginal] = useState<boolean>(false);
  const [selectedMediaContentType, setSelectedMediaContentType] = useState<string>('');
  const [isQuickTipsOpen, setIsQuickTipsOpen] = useState<boolean>(false);
  const [selectedSwap, setSelectedSwap] = useState<SwapResponse | null>(null);

  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [showVideoWarning, setShowVideoWarning] = useState(true);

  // Delete Confirmation Modal
  const [deleteSelectedSwapId, setDeleteSelectedSwapId] = useState<string | null>(null);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);

  // Upload State
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // User Info
  const user = useGetUser();

  // Downloading files
  const [isDownloadingSwap, setIsDownloadingSwap] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Fetch swaps with applied filters
  const swapsQuery = useQuerySwaps(
    cursor,
    ITEMS_PER_PAGE,
    isFavoriteSelected,
    [
      isMediaTypeImageSelected ? 'image' : null,
      isMediaTypeVideoSelected ? 'video' : null,
    ].filter(Boolean) as string[],
  );

  // Swap Count Query
  const swapCountInProgressQuery = useGetSwapCountInProgress();

  // Mutations
  const createSwapMutation = useCreateSwap();
  const deleteSwapMutations = useDeleteSwap();
  const updateSwapStatusMutation = useUpdateSwapStatus();

  const { data: profiles } = useGetPersonaProfiles();

  // Find the default "primary" profile or fallback to the first
  const primaryProfile = profiles?.find((p) => p.type === 'primary');
  const [selectedProfileId, setSelectedProfileId] = useState<string>(primaryProfile?.id || '');

  // If profiles change, ensure we still have a valid selection
  useEffect(() => {
    const newPrimary = profiles?.find((p) => p.type === 'primary');
    if (newPrimary) {
      setSelectedProfileId((prev) => {
        const stillExists = profiles?.some((pr) => pr.id === prev);
        return stillExists ? prev : newPrimary.id;
      });
    }
  }, [profiles]);

  // Currently selected profile object
  const currentProfile = profiles?.find((p) => p.id === selectedProfileId);

  useEffect(() => {
    if (locationData?.showCreateNeofaceSurvey) {
      setFeedbackOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [locationData]);

  // Handles updating the video ref to force ios load
  // Force load first frame on iOS
  useEffect(() => {
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.load();
      }
    }, 0);
  }, [isPreviewModalOpen, showOriginal, selectedOriginalUrl, selectedMediaUrl]);

  useEffect(() => {
    setCurrentPage(getCurrentPage(cursor));
  }, [cursor]);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout;
    // Store the initial counts of processing and created swaps
    const initialCreated = swapCountInProgressQuery.data?.createdCount || 0;
    const initialProcessing = swapCountInProgressQuery.data?.processingCount || 0;
    let previousCreated = initialCreated;
    let previousProcessing = initialProcessing;

    const startPolling = async () => {
      try {
        const swapCount = await swapCountInProgressQuery.refetch();
        const currentCreated = swapCount.data?.createdCount || 0;
        const currentProcessing = swapCount.data?.processingCount || 0;

        // If either count decreased, some jobs changed so refetch
        if (currentCreated < previousCreated || currentProcessing < previousProcessing) {
          await swapsQuery.refetch();
          previousCreated = currentCreated;
          previousProcessing = currentProcessing;
        }

        // Only stop polling if both counts are 0
        if (currentCreated === 0 && currentProcessing === 0) {
          clearInterval(pollInterval);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };

    // Start polling if either initial count is greater than 0
    if (swapCountInProgressQuery.isSuccess && (initialCreated > 0 || initialProcessing > 0)) {
      pollInterval = setInterval(startPolling, 7000);
    }

    // Clean up the interval when the component unmounts or the session changes
    return () => {
      clearInterval(pollInterval);
    };
  }, [swapCountInProgressQuery]);

  const openCrispChatOnUploadError = () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:open']);
      window.$crisp.push(["set", "message:text", ["I'm having trouble uploading a file"]]);
      window.$crisp.push(["set", "user:email", user.data?.email]);
    }
  };

  /**
   * Handle file select
   * @param files
   */
  const handleFileSelect = (files: FileList | null) => {
    if (files) {
      const filesArray = Array.from(files);
      if (selectedFiles.length + filesArray.length <= MAX_UPLOADS) {
        setSelectedFiles((prevFiles) => [...prevFiles, ...filesArray]);
      } else {
        toast.warning(`You can only upload ${MAX_UPLOADS} files at a time.`, { position: 'top-center' });
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Handle drop event
   * @param e
   */
  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  /**
   * Handle file input change
   * @param e
   */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  };

  /**
   * Get video duration
   * @param file
   */
  const getVideoDuration = (file: File): Promise<number | null> => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('video/')) {
        resolve(null);
        return;
      }

      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };

      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(null);
      };

      video.src = URL.createObjectURL(file);
    });
  };

  /**
   * Handle swap button click
   */
  const handleSwapClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      e.preventDefault();
      setIsUploading(true);

      // Get duration for each file (will be null for non-video files)
      const filePromises = selectedFiles.map(async (file) => {
        const duration = await getVideoDuration(file);
        return {
          file,
          contentType: file.type,
          ...(duration !== null && { duration }),
        };
      });

      const fileData = await Promise.all(filePromises);
      const filesToUpload = fileData.map(({ contentType, duration }) => ({
        contentType,
        ...(duration !== null && { duration }),
      }));

      const swapBody: CreateSwapsParams = {
        swaps: filesToUpload
      }

      if(currentProfile?.personaId) {
        swapBody.personaId = currentProfile.personaId;
      }

      const swapResponse = await createSwapMutation.mutateAsync(swapBody);
      const totalFiles = selectedFiles.length;
      const individualProgress = Array(totalFiles).fill(0);

      // Track upload results
      const uploadResults = await Promise.all(
        swapResponse.map(async (swap, index) => {
          const { file } = fileData[index];
          const { id: swapId, uploadUrl } = swap;

          try {
            await axios.put(uploadUrl, file, {
              headers: {
                'Content-Type': file.type,
                'x-goog-meta-swap-id': swapId.toString(),
                'x-goog-meta-persona-id': swapBody.personaId,
              },
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  individualProgress[index] = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                  );
                  const overallProgress = Math.round(
                    individualProgress.reduce((a, b) => a + b, 0) / totalFiles
                  );
                  setUploadProgress(Math.min(overallProgress, 100));
                }
              },
            });

            return { swapId, success: true };
          } catch (error) {
            let errorMessage: string;
            if (axios.isAxiosError(error)) {
              errorMessage = `Upload Error: ${
                error.response?.data?.message || error.message || 'Failed to upload file'
              }`;
            } else if (error instanceof Error) {
              errorMessage = `Upload Error: ${error.message}`;
            } else {
              errorMessage = 'Upload Error: An unknown error occurred';
            }
            return {
              swapId,
              success: false,
              errorMessage
            };
          }
        })
      );

      // Update status for failed uploads only
      const failedUploads = uploadResults.filter(result => !result.success);

      if (failedUploads.length === 0) {
        // Clear all selected files if everything was successful
        setSelectedFiles([]);
      } else {
        // Get the indices of failed uploads to keep those files
        const failedIndices = failedUploads.map(upload =>
          uploadResults.findIndex(result => result.swapId === upload.swapId)
        );

        // Keep only the failed files in selectedFiles
        setSelectedFiles(prev => prev.filter((_, index) => failedIndices.includes(index)));

        // Handle failed uploads
        toast.error(`There was an error uploading one of your files`);
        openCrispChatOnUploadError()
        await Promise.all(
          failedUploads.map(({ swapId, errorMessage }) =>
            updateSwapStatusMutation.mutateAsync({
              swapId,
              status: 'failed',
              failureReason: errorMessage,
              isUploadFailure: true,
            })
          )
        );
      }
    } catch (error) {
      console.error('Error in upload setup:', error);
    } finally {
      await swapsQuery.refetch();
      await swapCountInProgressQuery.refetch();
      setIsUploading(false);
    }
  };

  /**
   * Handle delete swap
   * @param swapId
   */
  const handleDeleteSwap = async (swapId: string) => {
    setDeleteSelectedSwapId(swapId);
    setIsConfirmDeleteModalOpen(true);
  };

  /**
   * Handle confirm delete
   */
  const handleConfirmDelete = async () => {
    if (deleteSelectedSwapId) {
      await deleteSwapMutations.mutateAsync({ swapId: deleteSelectedSwapId });
      await swapsQuery.refetch();

      // Optionally refetch data or update local state
      setDeleteSelectedSwapId(null);

    }
  };

  /**
   * Handle cancel delete
   */
  const handleCancelDelete = () => {
    setDeleteSelectedSwapId(null);
    setIsConfirmDeleteModalOpen(false);
  };

  /**
   * Handle remove file
   * @param index
   */
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  /**
   * Open the modal when a user clicks on the swap so they can compare
   * @param mediaUrl
   * @param targetUrl
   * @param contentType
   * @param swap
   */
  const handleOpenModal = (mediaUrl: string, targetUrl: string | null, contentType: string = 'image/jpg', swap: SwapResponse) => {
    setSelectedMediaUrl(mediaUrl);
    setSelectedOriginalUrl(targetUrl);
    setSelectedMediaContentType(contentType);
    setShowOriginal(false);
    setSelectedSwap(swap);
    setIsPreviewModalOpen(true);
  };

  /**
   * Reset modal state on close
   */
  const handleModalClose = () => {
    setIsPreviewModalOpen(false);
    setSelectedSwap(null);
    setSelectedMediaUrl('');
    setSelectedOriginalUrl('');
    setShowOriginal(false);
    setSelectedMediaContentType('');
  };

  /**
   * Handle page change
   * @param page
   */
  const handlePageChange = (page: number) => {
    const offset = (page - 1) * ITEMS_PER_PAGE;
    const newCursor = offset > 0 ? `offset=${offset}` : null;
    setCursor(newCursor);
  };

  /**
   * Get current page number
   * @param cursor
   */
  const getCurrentPage = (cursor: string | null): number => {
    if (!cursor) return 1;
    const offset = parseInt(new URLSearchParams(cursor).get('offset') || '0');
    return Math.floor(offset / ITEMS_PER_PAGE) + 1;
  };

  // Handlers for filter updates
  // We set cursor to null so that we-refetch from the beginning to get an accurate count
  const handleToggleFavorite = () => {
    setIsFavoriteSelected(prev => !prev);
    setCursor(null);
  };

  const handleToggleMediaTypeImage = () => {
    setIsMediaTypeImageSelected(prev => !prev);
    // setCursor(null);
  };
  const handleToggleMediaTypeVideo = () => {
    setIsMediaTypeVideoSelected(prev => !prev);
    // setCursor(null);
  };

  const handleClearFilters = () => {
    setIsFavoriteSelected(false);
    setIsMediaTypeImageSelected(false);
    setIsMediaTypeVideoSelected(false);
  };

  /**
   * Handle download swap state
   *
   * @param swapId
   */
  const handleDownloadSwapState = async (swapId: string | null) => {
    setIsDownloadingSwap(true);
    await handleDownloadSwap(swapId);
    setIsDownloadingSwap(false);
  };

  const isAnyFilteredEnabled = isFavoriteSelected || isMediaTypeImageSelected || isMediaTypeVideoSelected;

  if (!user.data?.persona?.id) {
    return (
      <div className='flex flex-col items-center justify-center'>
        <h1 className='text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold mb-4'>
          Anonymize Content
        </h1>
        <p className='text-gray-600 mb-8'>You must first create a neoface before you can begin anonymizing</p>
        <Link
          to='/create-neoface'
          className='inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
        >
          Create Neoface
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className='sm:flex sm:justify-between sm:items-center mb-6'>
        {/* Left: Title */}
        <div className='sm:mb-0'>
          <h1 className='text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold'>
            Anonymize Content
          </h1>
          <p>Anonymize your content using your neoface. Just start uploading your images and videos!</p>
        </div>
      </div>

      {/* Video Quality Notice - Subtle Banner */}
      {showVideoWarning && (
        <div className="mb-4 bg-amber-50 border-l-4 border-amber-300 px-3 py-2 text-sm text-gray-600 flex justify-between items-center">
          <span className="flex items-center">
            <VideoCameraIcon className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
            Video quality will be improved with our upcoming model update.
          </span>
          <button
            onClick={() => setShowVideoWarning(false)}
            className="text-gray-500 hover:text-gray-700 ml-2"
          >
            <span className="text-xl">×</span>
          </button>
        </div>
      )}

      {/* Photo guidelines button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowGuidelinesModal(true)}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <InformationCircleIcon className="h-4 w-4 mr-1" />
          Photo & Video Guidelines
        </button>
      </div>

      {/* Upload Area */}
      <UploadArea
        isDragging={isDragging}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        selectedFiles={selectedFiles}
        handleDrop={handleDrop}
        handleFileInputChange={handleFileInputChange}
        handleRemoveFile={handleRemoveFile}
        handleSwapClick={handleSwapClick}
        fileInputRef={fileInputRef}
        swapsQuery={swapsQuery}
        isAnyFilteredEnabled={isAnyFilteredEnabled}
        user={user}
        MAX_UPLOADS={MAX_UPLOADS}
        setIsDragging={setIsDragging}
        setSelectedProfileId={setSelectedProfileId}
        selectedProfileId={selectedProfileId}
        currentProfile={currentProfile}
      />

      {/* Filter */}
      <div className='mt-8'>
        <FilterChips
          isFavoriteSelected={isFavoriteSelected}
          isMediaTypeImageSelected={isMediaTypeImageSelected}
          isMediaTypeVideoSelected={isMediaTypeVideoSelected}
          onToggleFavorite={handleToggleFavorite}
          onToggleMediaTypeImage={handleToggleMediaTypeImage}
          onToggleMediaTypeVideo={handleToggleMediaTypeVideo}
          onClearFilters={handleClearFilters}
        />
      </div>

      {/* Skeleton */}
      {swapsQuery.isLoading && (
        <div className='mt-16'>
          <ul className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4'>
            {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
              <li key={index} className='rounded-lg overflow-hidden'>
                {/* Match the aspect-square image container */}
                <div className='relative aspect-square'>
                  <div className='h-full w-full bg-gray-200 animate-pulse' />
                </div>

                {/* Match the content padding and layout */}
                <div className='p-4'>
                  {/* Status and date row */}
                  <div className='flex'>
                    <div className='grow'>
                      <div className='inline-flex rounded-md px-2 py-1 w-20 h-6 bg-gray-200 animate-pulse' />
                    </div>
                    <div className='w-32 h-6 bg-gray-200 animate-pulse' />
                  </div>

                  {/* Action buttons row */}
                  <div className='mt-4 flex justify-between'>
                    <div
                      className='inline-flex items-center px-4 py-2 rounded-md w-32 h-10 bg-gray-200 animate-pulse' />
                    <div className='inline-flex items-center w-10 h-10 bg-gray-200 animate-pulse rounded-md' />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Swaps List */}
      {!swapsQuery.isLoading && (swapsQuery.data && swapsQuery.data.count > 0) && (
        <div className='mt-8'>
          <ul className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4'>
            {swapsQuery.data?.results.map((swap) => (
              <SwapCard
                key={swap.id}
                swap={swap}
                handleDeleteSwap={handleDeleteSwap}
                deleteSwapMutations={deleteSwapMutations}
                handleOpenModal={handleOpenModal}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Empty State for Swaps List */}
      {!swapsQuery.isLoading && (swapsQuery.data && swapsQuery.data.count < 1) && (
        <div className='mt-8 rounded-lg bg-white'>
          <div className='px-6 py-14 text-center'>
            <p className='text-sm text-gray-500'>
              {isAnyFilteredEnabled
                ? 'No results match your current filters'
                : 'No anonymized content yet. Your anonymized photos and videos will appear here.'}
            </p>
            {isAnyFilteredEnabled && (
              <button
                onClick={handleClearFilters}
                className='mt-4 text-sm text-indigo-600 hover:text-indigo-500'
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Desktop Pagination: Centered with better spacing */}
      <Pagination
        totalCount={swapsQuery.data?.count || 0}
        itemsPerPage={ITEMS_PER_PAGE}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        isLoading={swapsQuery.isLoading}
      />

      {/*  This Modal is for when a user clicks on swap card, and we show a utility modal that allows them to preview the full image, download, etc*/}

      {isPreviewModalOpen &&
        <SwapPreviewModal
          isOpen={isPreviewModalOpen}
          onClose={handleModalClose}
          mediaUrl={selectedMediaUrl}
          originalUrl={selectedOriginalUrl}
          showOriginal={showOriginal}
          onToggleOriginal={() => setShowOriginal(!showOriginal)}
          mediaContentType={selectedMediaContentType}
          onDownload={() => handleDownloadSwapState(selectedSwap?.id || null)}
          isDownloading={isDownloadingSwap}
          videoRef={videoRef}
          selectedSwap={selectedSwap}
        />
      }

      {showGuidelinesModal &&
        <PhotoGuidelinesModalAnonymize
          isOpen={showGuidelinesModal}
          onClose={() => setShowGuidelinesModal(false)}
        />
      }

      {isFeedbackOpen &&
        <FeedbackModal
          isOpen={isFeedbackOpen}
          onClose={() => setFeedbackOpen(false)}
        />
      }

      <ConfirmModal
        isOpen={isConfirmDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        message={'Are you sure you want to delete this item? This action cannot be undone.'}
      />
    </>
  );
}